interface Accommodation {
  id: number;
  name: string;
  area: string;
  tag: string;
  hashtags: string[];
  rating: number;
  reviewCount: number;
  price: string;
  maxGuests: number;
  beds: number;
  features: string[];
  airbnbUrl: string;
  hostName: string;
  description?: string;
  locationDesc?: string;
}

interface Props {
  accommodation: Accommodation;
}

const amenityIcons: Record<string, string> = {
  "와이파이": "ri-wifi-line",
  "넷플릭스": "ri-netflix-line",
  "세탁기": "ri-t-shirt-line",
  "에어컨": "ri-temp-cold-line",
  "주방": "ri-fridge-line",
  "주차": "ri-car-line",
  "발코니": "ri-landscape-line",
  "욕조": "ri-drop-line",
  "카카오톡": "ri-chat-1-line",
};

function getIcon(feature: string): string {
  for (const key of Object.keys(amenityIcons)) {
    if (feature.includes(key)) return amenityIcons[key];
  }
  return "ri-checkbox-circle-line";
}

export default function DetailInfo({ accommodation }: Props) {
  return (
    <div className="flex-1 min-w-0">
      {/* Title */}
      <div className="mb-5">
        <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3">
          {accommodation.hashtags.map((tag) => (
            <span key={tag} className="text-xs text-amber-700 bg-amber-50 px-2 sm:px-2.5 py-1 rounded-full font-medium">
              {tag}
            </span>
          ))}
        </div>
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-900 leading-tight mb-3">
          {accommodation.name}
        </h1>
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-star-fill text-amber-400 text-base"></i>
            </div>
            <span className="font-bold text-stone-800">{accommodation.rating}</span>
            <span className="text-stone-400 text-sm">({accommodation.reviewCount}개 후기)</span>
          </div>
          <div className="flex items-center gap-1.5 text-stone-500 text-sm">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-user-line text-sm"></i>
            </div>
            최대 {accommodation.maxGuests}인
          </div>
          <div className="flex items-center gap-1.5 text-stone-500 text-sm">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-hotel-bed-line text-sm"></i>
            </div>
            침실 {accommodation.beds}개
          </div>
          <div className="flex items-center gap-1.5 text-stone-500 text-sm">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-line text-sm"></i>
            </div>
            {accommodation.area}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-stone-100 mb-6" />

      {/* Host */}
      <div className="flex items-center gap-3 mb-6 p-4 bg-amber-50 rounded-xl">
        <div className="w-11 h-11 flex items-center justify-center bg-amber-500 rounded-full text-white font-bold text-base shrink-0">
          {accommodation.hostName.charAt(0)}
        </div>
        <div>
          <div className="text-sm font-semibold text-stone-800">{accommodation.hostName}</div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-xs text-amber-700 font-medium flex items-center gap-1">
              <i className="ri-shield-check-line text-xs"></i>
              한국어 소통 100%
            </span>
            <span className="text-xs text-stone-400 flex items-center gap-1">
              <i className="ri-chat-1-line text-xs"></i>
              카카오톡 상담 가능
            </span>
          </div>
        </div>
      </div>

      {/* Description */}
      {accommodation.description && (
        <div className="mb-6">
          <h2 className="text-lg font-bold text-stone-800 mb-3 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-file-text-line text-amber-500"></i>
            </div>
            숙소 소개
          </h2>
          <div className="text-stone-600 text-sm leading-relaxed space-y-3">
            {accommodation.description.split("\n\n").map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      )}

      {/* Features */}
      <div className="mb-6">
        <h2 className="text-lg font-bold text-stone-800 mb-3 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-home-gear-line text-amber-500"></i>
          </div>
          주요 편의 시설
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {accommodation.features.map((f) => (
            <div key={f} className="flex items-center gap-2 text-sm text-stone-600 bg-stone-50 rounded-lg px-3 py-2.5">
              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                <i className={`${getIcon(f)} text-amber-500 text-sm`}></i>
              </div>
              <span className="leading-snug">{f}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Location */}
      {accommodation.locationDesc && (
        <div className="mb-6">
          <h2 className="text-lg font-bold text-stone-800 mb-3 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-map-pin-2-line text-amber-500"></i>
            </div>
            위치 안내
          </h2>
          <div className="bg-stone-50 rounded-xl p-4">
            {accommodation.locationDesc.split(" / ").map((loc, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-stone-600 mb-2 last:mb-0">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                  <i className="ri-walk-line text-amber-400 text-xs"></i>
                </div>
                {loc}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trust */}
      <div className="bg-stone-50 rounded-xl p-4 mb-6">
        <h3 className="text-sm font-bold text-stone-700 mb-3">한울 안심 보장</h3>
        <div className="grid grid-cols-3 gap-2">
          {[
            { icon: "ri-translate-2", text: "한국어 소통" },
            { icon: "ri-shield-user-line", text: "비대면 체크인" },
            { icon: "ri-message-2-line", text: "카카오톡 상담" },
          ].map((item) => (
            <div key={item.text} className="flex flex-col items-center text-center gap-1 px-1">
              <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-amber-100 rounded-full">
                <i className={`${item.icon} text-amber-600 text-sm sm:text-base`}></i>
              </div>
              <span className="text-[10px] sm:text-xs text-stone-600 font-medium leading-snug">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
